import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLanguage, faHandsHelping, faStore, faVideo, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import { Image, Button } from "@nextui-org/react";
import { motion } from "framer-motion";
import RelatedLinks from '../components/RelatedLinks';
import { getImageUrl } from '../utils/imageUrl';
import './Home.css';

const servicesData = [
  {
    title: "展会中德翻译服务",
    description: "专业的德语、英语口译，助您无障碍沟通，拓展商机。",
    img: "/images/translator.png",
    icon: faLanguage
  },
  {
    title: "物流现场协助",
    description: "展品运输、清关、入馆、撤展？我们提供现场协调与支持。",
    img: "/images/cargoservice.png",
    icon: faHandsHelping
  },
  {
    title: "代参展服务",
    description: "无法亲临现场？我们帮您打理展位，接待客户，收集信息。",
    img: "/images/boothalone.png",
    icon: faStore
  },
  {
    title: "代逛展服务",
    description: "替您高效逛展，收集目标展商资料、名片和行业动态。",
    img: "/images/namecard.png",
    icon: faVideo
  }
];

const Home: React.FC = () => {
  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "德国展会中德翻译与现场协助",
    "provider": {
      "@type": "Organization",
      "name": "DECN Exhibition Services"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Germany"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "展会服务项目",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "展会中德翻译"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "翻译陪同"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "德国展会中文翻译"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "代参展"
          }
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>德国展会翻译 | 展会中德翻译陪同 | 德国展会中文翻译与代展 - DECN</title>
        <meta name="description" content="专业提供全德国（法兰克福、汉诺威、科隆、慕尼黑）展会中德翻译、翻译陪同、中德英口译、代参展、代逛展及现场物流协助。资深留学生团队，按产品背景指派德国展会中文翻译，助力企业深耕德国市场。" />
        <meta name="keywords" content="德国展会翻译, 展会中德翻译, 德国展会中文翻译, 翻译陪同, 法兰克福展会翻译, 汉诺威工博会翻译, 科隆展会翻译, 德国中德翻译, 德国代参展, 展会现场协助, 德国留学生翻译" />
        <link rel="canonical" href="https://decn-exhibition.top/" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <section
        className="hero"
        style={{
          background: `linear-gradient(120deg, rgba(76, 44, 137, 0.7) 0%, rgba(120, 69, 207, 0.7) 100%), url('${getImageUrl('/images/index_cologne.jpg', { width: 1600, format: 'webp', quality: 70 })}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container">
          <div className="hero-content">
            <h1>全场景德国展会服务</h1>
            <h2>展会中德翻译、翻译陪同、代参展、展会现场协助</h2>
            <p>我们是由德国留学生组成的专业展会服务团队，为您提供高质量的**德国展会中文翻译**、现场协助及代参展服务</p>
            <div className="cta-buttons">
              <a href="#services" className="btn btn-primary">了解服务</a>
              <Link to="/contact" className="btn btn-secondary">联系我们</Link>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2>我们的服务</h2>
            <p>为您提供全方位的德国展会服务</p>
          </div>
          <div className="services-grid">
            {servicesData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="service-card-container"
              >
                <div className="service-card">
                  <div className="service-header">
                    <FontAwesomeIcon icon={item.icon} className="service-icon" />
                    <h4 className="service-title">{item.title}</h4>
                  </div>
                  <div className="service-image-container">
                    <div className="service-image-wrapper">
                      <Image
                        removeWrapper
                        alt={item.title}
                        className="service-image"
                        src={item.img}
                      />
                    </div>
                  </div>
                  <div className="service-content">
                    <p className="service-description">{item.description}</p>
                    <div className="service-footer">
                      <Button size="sm" as={Link} to="/contact" variant="flat" className="service-button">了解详情</Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-preview">
        <div className="container">
          {/* Removed about-image div and adjusted about-content for centering */}
          <div className="about-content justify-center text-center"> {/* Added justify-center and text-center */}
            {/* <div className="about-image">
              <img src="/images/team.jpg" alt="DECN德国展会协助团队" />
            </div> */}
            <div className="about-text max-w-2xl"> {/* Added max-width for better readability */}
              <h3>关于我们</h3>
              <p>DECN德国展会协助是由在德国留学和工作的中国留学生组成的专业团队，我们熟悉德国文化和商业环境，能够为您提供专业的**翻译陪同**和展会服务。</p>
              <ul className="inline-block text-left"> {/* Adjusted ul for centering */}
                <li><FontAwesomeIcon icon={faCheck} /> <strong>专业翻译团队</strong> - 提供精准的**展会中德翻译**</li>
                <li><FontAwesomeIcon icon={faCheck} /> <strong>丰富展会经验</strong> - 服务过众多行业展会</li>
                <li><FontAwesomeIcon icon={faCheck} /> <strong>覆盖全德国</strong> - 科隆、汉诺威、法兰克福等主要展会城市</li>
                <li><FontAwesomeIcon icon={faCheck} /> <strong>灵活服务方案</strong> - 根据您的需求定制服务</li>
              </ul>
              <Link to="/about" className="btn btn-primary">了解更多</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Removed exhibitions section */}
      
      <RelatedLinks currentPage="/" />
    </>
  );
};

export default Home;